"use client";
import { Shield, Users, Star, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { useIsMobile } from '@/hooks/use-mobile';

interface TrustSignalsHeroProps {
  showAnimation?: boolean;
  variant?: 'compact' | 'full';
}

export function TrustSignalsHero({
  showAnimation = true,
  variant = 'full'
}: TrustSignalsHeroProps) {
  const isMobile = useIsMobile();

  if (variant === 'compact') {
    return (
      <div className="flex flex-wrap items-center gap-4 justify-center py-4">
        <Badge variant="secondary" className="gap-1.5 px-3 py-1.5">
          <Star className="h-3.5 w-3.5 fill-primary text-primary" />
          <span className="font-medium">4.9/5 on G2</span>
        </Badge>
        
        <Badge variant="secondary" className="gap-1.5 px-3 py-1.5">
          <Users className="h-3.5 w-3.5" />
          <span className="font-medium">1,000+ companies</span>
        </Badge>
        
        <Badge variant="secondary" className="gap-1.5 px-3 py-1.5">
          <Shield className="h-3.5 w-3.5" />
          <span className="font-medium">SOC 2 Certified</span>
        </Badge>
      </div>
    );
  }

  return (
    <div className="pt-2 pb-8 border-b bg-muted/20">
      <div className="container mx-auto max-w-7xl px-4">
        {/* Rating Badges Row */}
        <div className="text-center mb-4">
          <p className="text-sm text-muted-foreground font-medium">
            Hirebee is a highly recommended global hiring solution
          </p>
        </div>

        {(() => {
          const platforms = [
            { name: 'G2', score: '4.9', stars: 5, href: 'https://www.g2.com/products/hirebee-ai/reviews#reviews' },
            { name: 'Capterra', score: '4.8', stars: 5, href: 'https://www.capterra.com/p/201026/Hirebee/reviews/' },
            { name: 'TrustRadius', score: '4.5', stars: 4.5, href: 'https://www.trustradius.com/products/hirebee-ai/reviews' },
            { name: 'Software Advice', score: '4.8', stars: 5, href: 'https://www.capterra.com/p/201026/Hirebee/reviews/' },
            { name: 'GetApp', score: '4.8', stars: 5, href: 'https://www.getapp.com/hr-employee-management-software/a/hirebee/' },
          ];

          const renderBadge = (platform: typeof platforms[0]) => (
            <a
              key={platform.name}
              href={platform.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
              <span className="font-semibold text-sm">{platform.score}/5</span>
              <span className="text-muted-foreground text-sm">on {platform.name}</span>
            </a>
          );

          // Only one layout is ever mounted (not both, CSS-hidden) — a hidden
          // Carousel still runs embla's layout-measuring init on every load,
          // including desktop, which was a needless forced-reflow contributor.
          return isMobile ? (
            <Carousel opts={{ loop: true, align: 'center' }} className="w-full">
              <CarouselContent className="-ml-2">
                {platforms.map((platform) => (
                  <CarouselItem key={platform.name} className="pl-2 basis-auto">
                    {renderBadge(platform)}
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
              {platforms.map((platform) => renderBadge(platform))}
            </div>
          );
        })()}

      </div>
    </div>
  );
}
